import React, { useRef, useState } from 'react';
import { Form, Select, Input, Row, Col, Button, Modal, Space, Alert, message } from 'antd';
import SectionCard, { SectionColorScheme } from './SectionCard';
import { COUNTRY_OPTIONS } from './AddressFields';
import AddressBookImportModal from './AddressBookImportModal';
import { parseAddressText } from '../../../services/logistics';
import type { ParsedAddressResult, CustomerAddress } from '../../../services/logistics';

/** 解析结果字段 -> 表单字段名的映射；country 单独处理（USA -> US） */
const PARSED_FIELD_MAP: Record<string, string> = {
  recipient_name: 'name',
  address_line_1: 'address_line',
  address_line_2: 'address_line2',
  city: 'city',
  state: 'state',
  postal_code: 'postal_code',
  phone: 'phone',
  email: 'email',
};

// 美国电话号码归一化：只处理"号码前多打了国家代码1"这一种情况（比如 "+1 212-555-0100"、
// "1-212-555-0100" 这类很常见的写法），去掉之后变成标准的10位本地号码；除此之外的位数
// （9位、12位这种）不是"多打了国家代码"能解释的，原样返回，交给外面的位数校验去拦截——
// 不在这里做更多猜测性的修正，猜错了会把号码真的改坏，比直接报错更糟。
// 只应该在这个地址 country==='US' 时调用——中国大陆手机号本来就是11位、也经常以1开头
// （如 13812345678），对它做同样的处理会被误判成"多打了美国国家代码"，硬生生砍掉一位。
const normalizeUsPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
};

interface WarehouseOption {
  value: number;
  label: string;
}

interface PartyInfoPanelProps {
  title: string;
  colorScheme: SectionColorScheme;
  /** 对应 Form 里嵌套字段的前缀：['origin'] / ['destination'] */
  namePath: string[];
  /** true：收件人信息；false：发件人信息（决定姓名字段的标签文案） */
  isRecipient: boolean;
  /** 从地址库导入时用哪个地址类型筛选（'sender'/'recipient'，两者都会把"发件人或收件人"的记录一并带出来） */
  addressType: 'sender' | 'recipient';
  /** 快速询价开启时，只显示国家+邮编这一行，其余行隐藏 */
  quickQuote: boolean;
  /** 传入时，只有这些国家/地区可选，其余选项禁用（保留可见，仅不可选） */
  restrictCountriesTo?: string[];
  warehouseOptions: WarehouseOption[];
  warehousesLoading: boolean;
  warehousePlaceholder: string;
  onSelectWarehouse: (warehouseId: number | undefined) => void;
}

const PartyInfoPanel: React.FC<PartyInfoPanelProps> = ({
  title,
  colorScheme,
  namePath,
  isRecipient,
  addressType,
  quickQuote,
  restrictCountriesTo,
  warehouseOptions,
  warehousesLoading,
  warehousePlaceholder,
  onSelectWarehouse,
}) => {
  const nameRequired = !quickQuote;
  const countryOptions = restrictCountriesTo
    ? COUNTRY_OPTIONS.map((option) => ({ ...option, disabled: !restrictCountriesTo.includes(option.value) }))
    : COUNTRY_OPTIONS;

  const form = Form.useFormInstance();
  const country = Form.useWatch([...namePath, 'country'], form);
  const [parseModalOpen, setParseModalOpen] = useState(false);
  const [parseText, setParseText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  // 邮编校验（仅美国）：复用地址解析接口，传入邮编反查城市/州；
  // 查不到 -> 提示核实；查得到但和已填的城市/州对不上 -> 提示是否按邮编纠正。
  const zipLookupTimerRef = useRef<number | null>(null);
  const zipIgnoreWarningRef = useRef<Set<string>>(new Set());
  const zipInvalidModalOpenRef = useRef(false);
  const zipMismatchModalRef = useRef<{ zip: string; ts: number }>({ zip: '', ts: 0 });

  const scheduleZipLookup = (zipRaw: string) => {
    const zip = String(zipRaw || '').trim();
    if (!zip || !/^\d{5}(?:-\d{4})?$/.test(zip)) return;
    const isZipPlus4 = /^\d{5}-\d{4}$/.test(zip);
    if (zipLookupTimerRef.current) {
      window.clearTimeout(zipLookupTimerRef.current);
    }
    zipLookupTimerRef.current = window.setTimeout(async () => {
      try {
        const response = await parseAddressText(zip);
        const parsed: ParsedAddressResult = response.data?.parsed || {};
        const hasMatch = !!(parsed.city || parsed.state || parsed.zip_lookup_city || parsed.zip_lookup_state);
        if (!hasMatch) {
          if (zipIgnoreWarningRef.current.has(zip)) return;
          if (!zipInvalidModalOpenRef.current) {
            zipInvalidModalOpenRef.current = true;
            Modal.confirm({
              title: `未查询到匹配的邮编信息 [已输入邮编: ${zip}]，请核实邮政编码是否正确。`,
              okText: '继续使用该邮编',
              cancelText: '关闭',
              maskClosable: false,
              keyboard: false,
              onOk: () => {
                zipIgnoreWarningRef.current.add(zip);
                zipInvalidModalOpenRef.current = false;
              },
              onCancel: () => {
                zipInvalidModalOpenRef.current = false;
              },
            });
          }
          return;
        }

        const normalizeCity = (v: string) => String(v || '').trim().toLowerCase().replace(/\./g, '');
        const normalizeState = (v: string) => String(v || '').trim().toUpperCase();
        const zipCity = String(parsed.zip_lookup_city || parsed.city || '').trim();
        const zipState = String(parsed.zip_lookup_state || parsed.state || '').trim();
        const currentValues = form.getFieldValue(namePath) || {};
        const currCity = String(currentValues.city || '').trim();
        const currState = String(currentValues.state || '').trim();
        const hasZipCheck = !!(zipCity || zipState);
        const mismatch = hasZipCheck && (
          (zipCity && currCity && normalizeCity(zipCity) !== normalizeCity(currCity))
          || (zipState && currState && normalizeState(zipState) !== normalizeState(currState))
        );
        if (!mismatch) return;

        const now = Date.now();
        if (zipMismatchModalRef.current.zip === zip && now - zipMismatchModalRef.current.ts < 2000) {
          return;
        }
        zipMismatchModalRef.current = { zip, ts: now };

        const picked: Record<string, string> = {};
        if (parsed.city) picked.city = parsed.city;
        if (parsed.state) picked.state = parsed.state;
        if (parsed.postal_code && !isZipPlus4) picked.postal_code = parsed.postal_code;
        if (Object.keys(picked).length === 0) return;

        Modal.confirm({
          title: '邮编自动匹配 请确认输入了正确的城市/州/邮编信息',
          width: 480,
          content: (
            <div>
              <div>已输入：城市 {currCity || '-'} | 州 {currState || '-'} | 邮编 {zip}</div>
              <div>邮编匹配：城市 {zipCity || '-'} | 州 {zipState || '-'} | 邮编 {zip}</div>
            </div>
          ),
          okText: '按邮编匹配填入',
          cancelText: '取消',
          onOk: () => {
            const latestValues = form.getFieldValue(namePath) || {};
            form.setFieldsValue({ [namePath[0]]: { ...latestValues, ...picked } });
          },
        });
      } catch (error) {
        console.error('邮编校验失败:', error);
      }
    }, 300);
  };

  const handleParse = async () => {
    if (!parseText.trim()) {
      message.warning('请先粘贴地址文本');
      return;
    }
    setParsing(true);
    try {
      const response = await parseAddressText(parseText);
      const parsed: ParsedAddressResult = response.data?.parsed || {};
      const currentValues = form.getFieldValue(namePath) || {};
      // 每次解析都用新结果覆盖对应字段的旧值；没有解析出结果的字段保留原有内容，不清空。
      const updates: Record<string, string> = {};
      Object.entries(PARSED_FIELD_MAP).forEach(([parsedKey, formKey]) => {
        const value = (parsed as Record<string, string | undefined>)[parsedKey];
        if (value) {
          updates[formKey] = value;
        }
      });
      if (parsed.country) {
        updates.country = parsed.country === 'USA' ? 'US' : parsed.country;
      }
      if (Object.keys(updates).length === 0) {
        message.info('没有识别到可以填入的字段');
        return;
      }
      form.setFieldsValue({ [namePath[0]]: { ...currentValues, ...updates } });
      message.success('已按解析结果填入');
      setParseModalOpen(false);
      setParseText('');
      // 解析出的邮编也走一遍和手动输入一样的校验，不单独维护一套逻辑
      if (updates.postal_code) {
        scheduleZipLookup(updates.postal_code);
      }
    } catch (error) {
      message.error('地址解析失败');
      console.error('地址解析失败:', error);
    } finally {
      setParsing(false);
    }
  };

  const handleImportAddress = (addr: CustomerAddress) => {
    // 正常情况下导入弹窗已经按 restrictCountriesTo 过滤过，选不出不允许的国家；这里再挡
    // 一道纯粹是防御性的（万一以后弹窗被复用到别的地方、忘了传这个 prop），不能让一个
    // 不支持的国家绕过下拉框的限制直接从这条路径混进表单。
    if (restrictCountriesTo && !restrictCountriesTo.includes(addr.country)) {
      message.error('该地址所在国家/地区暂不支持，未导入');
      return;
    }
    const currentValues = form.getFieldValue(namePath) || {};
    const updates = {
      country: addr.country,
      postal_code: addr.postal_code,
      name: addr.name,
      company: addr.company || '',
      address_line: addr.address_line,
      address_line2: addr.address_line2 || '',
      city: addr.city,
      state: addr.state,
      phone: addr.phone || '',
    };
    form.setFieldsValue({ [namePath[0]]: { ...currentValues, ...updates } });
    message.success('已从地址库导入');
    // 导入的邮编也走一遍和手动输入一样的校验，防止地址库里存的是过时/有误的数据
    if (addr.country === 'US' && addr.postal_code) {
      scheduleZipLookup(addr.postal_code);
    }
  };

  return (
    <SectionCard
      title={title}
      colorScheme={colorScheme}
      extraAlign="end"
      extra={(
        <Space>
          {/* 从地址库导入不挑国家，只要展开了完整地址字段就能用 */}
          {!quickQuote && (
            <Button size="small" onClick={() => setImportModalOpen(true)}>
              从地址库导入
            </Button>
          )}
          {/* 只有非快速询价（完整地址字段已展开）且国家是美国时才显示——
              解析逻辑本身是按美国地址书写习惯调的启发式规则，对其他国家的地址无法正确识别。 */}
          {!quickQuote && country === 'US' && (
            <Button size="small" onClick={() => setParseModalOpen(true)}>
              使用地址解析
            </Button>
          )}
          <Select
            style={{ width: 240 }}
            size="small"
            placeholder={warehousePlaceholder}
            loading={warehousesLoading}
            showSearch
            options={warehouseOptions}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            onChange={onSelectWarehouse}
            allowClear
          />
        </Space>
      )}
    >
      {/* 第1行：国家/地区 邮编 —— 快速询价状态下始终只显示这一行 */}
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            label="国家/地区"
            name={[...namePath, 'country']}
            rules={[{ required: true, message: '请选择国家/地区' }]}
          >
            <Select options={countryOptions} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="邮编"
            name={[...namePath, 'postal_code']}
            rules={[{ required: true, message: '请输入邮编' }]}
          >
            <Input onChange={(e) => { if (country === 'US') scheduleZipLookup(e.target.value); }} />
          </Form.Item>
        </Col>
      </Row>

      {!quickQuote && (
        <>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label={isRecipient ? '收件人姓名' : '发件人姓名'}
                name={[...namePath, 'name']}
                rules={[{ required: nameRequired, message: '请输入姓名' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="公司名称" name={[...namePath, 'company']}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="地址第一行"
                name={[...namePath, 'address_line']}
                rules={[{ required: nameRequired, message: '请输入详细地址' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={24}>
              <Form.Item label="地址第二行" name={[...namePath, 'address_line2']}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="城市"
                name={[...namePath, 'city']}
                rules={[{ required: nameRequired, message: '请输入城市' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="州/省"
                name={[...namePath, 'state']}
                rules={[{ required: nameRequired, message: '请输入州/省' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="电话"
                name={[...namePath, 'phone']}
                tooltip="美国电话号码为10位（多打的国家代码1会自动去掉）"
                rules={[
                  { required: nameRequired, message: '请输入电话' },
                  // 真实调用网关验证过：美国电话号码位数不对会导致部分承运商（比如 FedEx
                  // SmartPost）单独报错拒绝报价，位数太多同样可能导致网关那边报错——干脆在
                  // 表单这一层先拦住，不用等报价/购买失败了才回头怀疑是不是电话号码的问题。
                  // 收发件人都要校验，不只是收件人——USPS 真实遇到过因为发件人（origin）电话
                  // 位数不对导致购买失败，跟 FedEx SmartPost 挑收件人电话是同一类问题，只是
                  // 挑的是地址的另一侧，校验规则本身跟"是发件人还是收件人"无关，只跟这个地址
                  // 是不是美国有关——country!=='US' 时依然跳过，不影响从其它国家发货/收货。
                  // 校验前先做归一化（去掉多打的国家代码1），不是严格要求"必须一字不差正好
                  // 10位"——"+1 212-555-0100"这种很常见的写法是合法的，不应该被拒绝。
                  {
                    validator: (_: unknown, value: string) => {
                      if (country !== 'US' || !value) return Promise.resolve();
                      const digits = normalizeUsPhone(value);
                      return digits.length === 10
                        ? Promise.resolve()
                        : Promise.reject(new Error('美国电话号码应为10位（不含国家代码）'));
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="参考号" name={[...namePath, 'reference_number']}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      <Modal
        title="地址解析"
        open={parseModalOpen}
        onCancel={() => setParseModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12, background: '#fffbe6', border: '1px solid #ffe58f' }}
          message="地址解析仅适用于美国地址，其他国家地区地址请手动输入"
        />
        <Input.TextArea
          value={parseText}
          onChange={(e) => setParseText(e.target.value)}
          rows={5}
          placeholder="粘贴美国地址文本（如：姓名、地址、城市州邮编、电话）"
        />
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <Button type="primary" onClick={handleParse} loading={parsing}>
            解析并填入
          </Button>
        </div>
      </Modal>

      <AddressBookImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        addressType={addressType}
        restrictCountriesTo={restrictCountriesTo}
        onSelect={handleImportAddress}
      />
    </SectionCard>
  );
};

export default PartyInfoPanel;
